/**
 * Voice WebSocket Provider
 *
 * Integrates audio capture with WebSocket transmission.
 * Wraps the app to provide WebSocket connectivity.
 */

import { useEffect } from "react";

import { useSnapshot } from "valtio";
import { voiceAgentStore } from "../../../../../store/voiceAgentStore";
import { getAudioCapture } from "../../../../../utils/voice-agent/audioCapture";
import { useVoiceWebSocket } from "../../../../../hooks/useVoiceWebSocket";

export function VoiceWebSocketProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { sendAudioChunk, isConnected, connectionStatus } = useVoiceWebSocket();
  const { status } = useSnapshot(voiceAgentStore);

  // Update audio capture callback when WebSocket connection changes
  useEffect(() => {
    if (status === "connected" && isConnected) {
      console.log("✅ WebSocket connected - audio will be transmitted");

      // Update audio capture configuration to send chunks via WebSocket
      const audioCapture = getAudioCapture();
      if (audioCapture.isActive()) {
        audioCapture.updateConfig({
          onAudioChunk: (_, binaryData) => {
            // Send binary Uint8Array like the working demo
            sendAudioChunk(binaryData);
          },
        });

        const cfg = audioCapture.getConfig();
        const kbDecimal = (cfg.chunkSizeBytes / 1000).toFixed(1);
        const kib = (cfg.chunkSizeBytes / 1024).toFixed(2);
        console.log(
          `⚙️ Audio capture forwarding: chunkSizeBytes=${cfg.chunkSizeBytes} bytes (${kbDecimal} KB / ${kib} KiB), processorBufferSize=${cfg.processorBufferSize}`
        );
      }
    }
  }, [status, isConnected, sendAudioChunk]);

  // Log connection status changes
  useEffect(() => {
    console.log("🔌 WebSocket status:", connectionStatus);
  }, [connectionStatus]);

  return <>{children}</>;
}
